/**
 * FlyingEnemy - Aerial patrol enemy with wings
 * 
 * Flies in a sine-wave pattern, creating aerial hazards.
 * Can be defeated by landing on top (same as regular enemies).
 * Features animated wings and glowing eyes.
 * 
 * @module objects/FlyingEnemy
 */

import Phaser from 'phaser';

export default class FlyingEnemy {
    /**
     * @param {Phaser.Scene} scene - The game scene
     * @param {number} x - Spawn X position
     * @param {number} y - Spawn Y position
     * @param {Object} [options] - Configuration options
     * @param {number} [options.amplitude=80] - Vertical oscillation amplitude
     * @param {number} [options.frequency=0.002] - Oscillation frequency
     * @param {number} [options.speed=1.5] - Horizontal patrol speed
     * @param {number} [options.walkDist=150] - Horizontal patrol range
     * @param {number} [options.color=0x9933ff] - Body color
     */
    constructor(scene, x, y, options = {}) {
        this.scene = scene;
        this.startPos = { x, y };
        this.isDead = false;
        this.type = 'flying';

        // Configuration with defaults
        this.amplitude = options.amplitude ?? 80;
        this.frequency = options.frequency ?? 0.002;
        this.speed = options.speed ?? 1.5;
        this.walkDist = options.walkDist ?? 150;
        this.bodyColor = options.color ?? 0x9933ff;

        // Create visual components
        this.createVisuals(x, y);

        // Create physics body
        this.createPhysicsBody(x, y);

        // Animation state
        this.moveDir = 1;
        this.time = Math.random() * 1000; // Randomize phase
        this.wingAngle = 0;
    }

    /**
     * Create all visual elements using vector graphics
     */
    createVisuals(x, y) {
        // Wing graphics (drawn procedurally)
        this.leftWing = this.scene.add.graphics();
        this.rightWing = this.scene.add.graphics();
        this.drawWing(this.leftWing, x - 20, y, true);
        this.drawWing(this.rightWing, x + 20, y, false);

        // Main body - rounded diamond shape
        this.sprite = this.scene.add.graphics();
        this.drawBody(this.sprite, x, y);

        // Create a simple rect for physics attachment
        this.physicsSprite = this.scene.add.rectangle(x, y, 36, 28, 0x000000, 0);

        // Glowing eyes
        this.leftEye = this.scene.add.circle(x - 8, y - 2, 5, 0xffffff);
        this.rightEye = this.scene.add.circle(x + 8, y - 2, 5, 0xffffff);
        this.leftPupil = this.scene.add.circle(x - 7, y - 2, 2.5, 0xff0066);
        this.rightPupil = this.scene.add.circle(x + 9, y - 2, 2.5, 0xff0066);

        // Eye glow effect
        this.leftGlow = this.scene.add.circle(x - 8, y - 2, 8, 0xff0066, 0.15);
        this.rightGlow = this.scene.add.circle(x + 8, y - 2, 8, 0xff0066, 0.15);

        // Shadow (smaller, oval)
        this.shadow = this.scene.add.ellipse(x, y + 40, 28, 8, 0x000000, 0.2);

        // Pulsing glow animation on eyes
        this.scene.tweens.add({
            targets: [this.leftGlow, this.rightGlow],
            alpha: 0.35,
            scale: 1.3,
            duration: 800,
            yoyo: true,
            repeat: -1,
            ease: 'Sine.easeInOut'
        });
    }

    /**
     * Draw wing shape procedurally
     * @param {Phaser.GameObjects.Graphics} graphics
     * @param {number} x
     * @param {number} y
     * @param {boolean} isLeft
     */
    drawWing(graphics, x, y, isLeft) {
        graphics.clear();
        const dir = isLeft ? -1 : 1;
        graphics.fillStyle(this.bodyColor, 0.6);
        graphics.beginPath();
        graphics.moveTo(x, y);
        graphics.lineTo(x + dir * 18, y - 10);
        graphics.lineTo(x + dir * 22, y - 4);
        graphics.lineTo(x + dir * 16, y + 4);
        graphics.closePath();
        graphics.fill();

        // Wing highlight
        graphics.lineStyle(1, 0xffffff, 0.3);
        graphics.beginPath();
        graphics.moveTo(x, y);
        graphics.lineTo(x + dir * 18, y - 10);
        graphics.stroke();
    }

    /**
     * Draw body shape procedurally
     * @param {Phaser.GameObjects.Graphics} graphics
     * @param {number} x
     * @param {number} y
     */
    drawBody(graphics, x, y) {
        graphics.clear();

        // Body glow
        graphics.fillStyle(this.bodyColor, 0.15);
        graphics.fillCircle(x, y, 22);

        // Main body (rounded rect via circle)
        graphics.fillStyle(this.bodyColor, 1);
        graphics.fillRoundedRect(x - 16, y - 12, 32, 24, 8);

        // Body highlight
        graphics.fillStyle(0xffffff, 0.2);
        graphics.fillRoundedRect(x - 14, y - 10, 28, 10, 5);

        // Bottom shadow
        graphics.fillStyle(0x000000, 0.2);
        graphics.fillRoundedRect(x - 14, y + 4, 28, 6, 3);
    }

    /**
     * Create physics body using Matter.js
     */
    createPhysicsBody(x, y) {
        const { Bodies } = Phaser.Physics.Matter.Matter;
        const enemyBody = Bodies.rectangle(x, y, 36, 28, {
            isStatic: false,
            friction: 0,
            frictionAir: 0,
            density: 0.001,
            label: 'enemy',
            ignoreGravity: true
        });

        this.scene.matter.add.gameObject(this.physicsSprite, enemyBody);
        this.physicsSprite.setFixedRotation();
        this.physicsSprite.enemyObject = this;
    }

    /**
     * Update loop - sine wave flight pattern
     */
    update() {
        if (this.isDead || !this.physicsSprite.active) return;

        this.time += 16; // Approximate frame time

        // Horizontal patrol
        if (this.physicsSprite.x > this.startPos.x + this.walkDist) {
            this.moveDir = -1;
        } else if (this.physicsSprite.x < this.startPos.x - this.walkDist) {
            this.moveDir = 1;
        }

        // Sine wave vertical movement
        const yOffset = Math.sin(this.time * this.frequency) * this.amplitude;
        const targetY = this.startPos.y + yOffset;

        this.physicsSprite.setVelocityX(this.moveDir * this.speed);
        this.physicsSprite.setVelocityY((targetY - this.physicsSprite.y) * 0.08);

        // Update visuals position
        const x = this.physicsSprite.x;
        const y = this.physicsSprite.y;

        // Animate wings
        this.wingAngle += 0.15;
        const wingBeat = Math.sin(this.wingAngle) * 6;

        // Redraw wings with animation
        this.drawWing(this.leftWing, x - 16, y + wingBeat * 0.5, true);
        this.drawWing(this.rightWing, x + 16, y + wingBeat * 0.5, false);

        // Update body position
        this.drawBody(this.sprite, x, y);

        // Eyes follow position
        const pupilOffset = this.moveDir * 2;
        this.leftEye.setPosition(x - 8, y - 2);
        this.rightEye.setPosition(x + 8, y - 2);
        this.leftPupil.setPosition(x - 8 + pupilOffset, y - 2);
        this.rightPupil.setPosition(x + 8 + pupilOffset, y - 2);
        this.leftGlow.setPosition(x - 8, y - 2);
        this.rightGlow.setPosition(x + 8, y - 2);

        // Shadow follows with parallax offset
        this.shadow.setPosition(x, this.startPos.y + this.amplitude + 30);
        this.shadow.setAlpha(Math.max(0.05, 0.2 - Math.abs(yOffset) * 0.002));
    }

    /**
     * Kill this enemy with death effect
     */
    kill() {
        if (this.isDead) return;
        this.isDead = true;
        this.createDeathEffect();
        this.destroyVisuals();
    }

    /**
     * Create purple burst death effect
     */
    createDeathEffect() {
        const x = this.physicsSprite.x;
        const y = this.physicsSprite.y;

        // Feather burst particles
        for (let i = 0; i < 10; i++) {
            const angle = (i / 10) * Math.PI * 2;
            const speed = Phaser.Math.Between(30, 70);
            const feather = this.scene.add.ellipse(x, y, 8, 4, this.bodyColor, 0.8);

            this.scene.tweens.add({
                targets: feather,
                x: x + Math.cos(angle) * speed,
                y: y + Math.sin(angle) * speed - 20,
                alpha: 0,
                rotation: Phaser.Math.Between(-4, 4),
                scaleX: 0.2,
                duration: 600,
                ease: 'Power2',
                onComplete: () => feather.destroy()
            });
        }
    }

    /**
     * Safely destroy all visual elements
     */
    destroyVisuals() {
        const visuals = [
            this.physicsSprite, this.sprite,
            this.leftWing, this.rightWing,
            this.leftEye, this.rightEye,
            this.leftPupil, this.rightPupil,
            this.leftGlow, this.rightGlow,
            this.shadow
        ];

        visuals.forEach(v => {
            if (v && v.destroy && !v.destroyed) {
                v.destroy();
            }
        });

        this.physicsSprite = null;
        this.sprite = null;
    }

    /**
     * Full cleanup
     */
    destroy() {
        this.isDead = true;
        this.destroyVisuals();
    }
}
